// =============================================================================
// Auth slice — Phase 4A: secondary replicated state via dual-write.
//
// Source-of-truth invariant (LOAD-BEARING — do not violate without first
// reading the migration plan):
//
//   state.userLogin.userInfo  — CANONICAL. All consumers still read here.
//   state.auth                — secondary replica. Populated by:
//                                 - preloadedState (cold start)
//                                 - AUTH_LOGIN_SUCCESS (login/register)
//                                 - AUTH_TOKEN_REFRESH_SUCCESS (refresh)
//                                 - AUTH_LOGOUT (logout)
//
// Phase 4A guarantees the writes; Phase 5 migrates consumers; Phase 6 inverts
// ownership (legacy becomes derived projection of auth + profile).
//
// Reducer contract:
//   * idempotent  — same payload twice produces identical state
//   * reset-safe  — AUTH_LOGOUT returns the INITIAL ref unconditionally
//   * reload-safe — store recreation re-seeds via preloadedState; reducer
//                   default == INITIAL ref (consistent across reloads)
//
// Reading from auth.* during Phase 4A is permitted ONLY for:
//   - tests asserting the dual-write contract
//   - DevTools inspection
//   - Phase 5 PRs that explicitly migrate one consumer at a time
// Production code MUST continue reading state.userLogin.userInfo.
// =============================================================================

import {
  AUTH_LOGIN_SUCCESS,
  AUTH_TOKEN_REFRESH_SUCCESS,
  AUTH_LOGOUT,
  AUTH_IDENTITY_PATCH,
} from '../constants/authConstants';

const INITIAL_AUTH_STATE = {
  access: null,
  refresh: null,
  status: 'idle',
  error: null,
  // identity (lives here from Phase 6 onwards; today held in userLogin.userInfo)
  userId: null,
  publicId: null,
  email: null,
  isEmailVerified: false,
};

export const authReducers = (state = INITIAL_AUTH_STATE, action) => {
  switch (action.type) {
    case AUTH_LOGIN_SUCCESS: {
      // payload = raw backend response (snake_case). Map to camelCase auth shape.
      const blob = action.payload || {};
      return {
        access: blob.access ?? null,
        refresh: blob.refresh ?? null,
        status: 'success',
        error: null,
        userId: blob.id ?? null,
        publicId: blob.public_id ?? null,
        email: blob.email ?? null,
        isEmailVerified: blob.is_email_verified ?? false,
      };
    }

    case AUTH_TOKEN_REFRESH_SUCCESS:
      // INVARIANT (load-bearing — do NOT extend this case to write identity):
      //   AUTH_TOKEN_REFRESH_SUCCESS patches the access token ONLY. Identity
      //   fields (userId, publicId, email, isEmailVerified) and refresh token
      //   MUST survive the refresh untouched. The /token/refresh/ endpoint
      //   returns a single { access } and carries no identity payload — any
      //   future change to write identity here would either invent data or
      //   cross-pollinate from a different action's payload, both bugs.
      //
      // Identity refresh on session continuation belongs in AUTH_LOGIN_SUCCESS
      // (login/register/google) or in a future AUTH_IDENTITY_PATCH action,
      // never in this case.
      return {
        ...state,
        access: action.payload,
        error: null,
      };

    case AUTH_IDENTITY_PATCH: {
      // ===========================================================================
      // AUTH_IDENTITY_PATCH — INVARIANTS (load-bearing — read before extending)
      //
      //   * IDENTITY-DIMENSION ONLY.
      //     This case writes ONLY whitelisted identity fields. It NEVER writes:
      //       - access            (token, owned by AUTH_LOGIN_SUCCESS / refresh)
      //       - refresh           (token, owned by AUTH_LOGIN_SUCCESS)
      //       - status            (login lifecycle, owned by AUTH_LOGIN_SUCCESS)
      //       - error             (login lifecycle)
      //     Any future change that adds non-identity writes here is a contract
      //     violation — split into a separate action instead.
      //
      //   * NEVER WRITES access / refresh.
      //     The endpoints that produce AUTH_IDENTITY_PATCH payloads return
      //     UserSerializer (no token mixin) — there are no tokens to forward.
      //     Writing tokens from this case would either invent values or
      //     cross-pollute from another action's payload, both bugs.
      //
      //   * LOW-FREQUENCY RECONCILIATION ONLY.
      //     Designed for: email verification confirm (one-shot flip) and
      //     bootstrap hydration (once per app mount). Not for per-keystroke
      //     or per-render mutations. Hot paths must not dispatch this.
      //
      //   * NOT A GENERIC USER PATCH BUS.
      //     Profile mutations (name, handle, avatar, social links, follow,
      //     etc.) live in profile slice (Phase 4C+). This action does NOT
      //     accept profile fields and silently ignores them via the whitelist.
      //
      // -----------------------------------------------------------------------------
      // ALLOWED-FIELD WHITELIST (the ONLY keys honored from payload):
      //   - id                  → state.userId
      //   - public_id           → state.publicId
      //   - email               → state.email
      //   - is_email_verified   → state.isEmailVerified
      //
      // Any other key in payload is ignored at this layer. Backend additions
      // of new identity fields require simultaneous whitelist + reducer
      // update — no automatic propagation.
      // -----------------------------------------------------------------------------
      // PATCH-SEMANTIC (critical — distinguishes from AUTH_LOGIN_SUCCESS):
      //
      //   Field-presence check ('X' in blob) is used INSTEAD OF nullish-
      //   coalescing (blob.X ?? state.X) because the two are not equivalent:
      //
      //     missing key         → preserve current state value
      //     explicit null       → write null (e.g. email privacy-gated)
      //     false / 0 / ""      → written as-is (NOT confused with absent)
      //     true                → written as-is
      //
      //   Example: backend returns { is_email_verified: false } — this MUST
      //   write false, not preserve the previous true via nullish-coalescing.
      //   `?? state.isEmailVerified` would mishandle this only if false were
      //   coerced; in fact `??` handles false correctly. The discipline is
      //   maintained anyway because email CAN legitimately become null
      //   (privacy gate flip), and `null ?? state.email` would WRONGLY
      //   preserve old email.
      //
      // -----------------------------------------------------------------------------
      // BOOTSTRAP RECONCILIATION CONTRACT:
      //
      //   When dispatched by bootstrapApp on profile fetch success, this is
      //   EVENTUALLY-CONSISTENT IDENTITY RECONCILIATION ONLY — NOT
      //   AUTHORITATIVE SYNCHRONIZATION. Specifically:
      //
      //     - Latency: server identity changes that happen AFTER bootstrap
      //       fetch resolves are not reflected until the NEXT bootstrap
      //       (i.e. next page reload).
      //     - Direction: read-only mirror from server. This action does not
      //       push frontend changes back; that direction belongs to mutation
      //       endpoints + their dispatches.
      //     - Cross-slice gap: legacy state.userLogin.userInfo and state.auth
      //       receive separate writes from separate code paths — they may
      //       briefly disagree during a tick but converge by end-of-tick
      //       under current dispatch ordering.
      //
      // -----------------------------------------------------------------------------
      // TEMPORARY MIGRATION CAVEAT (Phase 4–5):
      //
      //   Legacy state.userLogin.userInfo remains AUTHORITATIVE through
      //   Phase 5; state.auth is a secondary replica. Therefore some identity
      //   updates may temporarily diverge across slices:
      //
      //     - validateUserProfile: USER_LOGIN_SUCCESS writes is_email_verified
      //       into legacy via mergeUserInfo BEFORE this AUTH_IDENTITY_PATCH
      //       dispatches. Two ticks of divergence inside one thunk.
      //     - bootstrapApp: profile.data updates BEFORE auth identity does.
      //
      //   Until Phase 6 ownership inversion, consumers MUST NOT cross-read
      //   between state.auth.X and state.userLogin.userInfo.X for the same
      //   semantic field expecting consistency. Read either, not both.
      // ===========================================================================
      const blob = action.payload || {};
      const patch = {};

      // Whitelisted fields — explicit-presence checks (NOT ??).
      if ('id' in blob) patch.userId = blob.id;
      if ('public_id' in blob) patch.publicId = blob.public_id;
      if ('email' in blob) patch.email = blob.email;
      if ('is_email_verified' in blob) {
        patch.isEmailVerified = blob.is_email_verified;
      }

      // Ref-stable no-op when nothing actually changes (every patch key
      // already equals current state). Avoids subscriber churn on bootstrap
      // for users whose identity hasn't changed since last persist (the
      // common case).
      const keys = Object.keys(patch);
      if (keys.length === 0) return state;
      if (keys.every((k) => patch[k] === state[k])) return state;

      return { ...state, ...patch };
    }

    case AUTH_LOGOUT:
      // Reset to INITIAL ref unconditionally. Idempotent: repeat dispatches
      // return the same ref → no subscriber churn.
      //
      // ⚠ HARD-RELOAD CONSTRAINT (Phase 4A — load-bearing):
      //   AUTH_LOGOUT is currently dispatched ONLY by paths that immediately
      //   trigger a full page reload (window.location.href = "/login"):
      //     - userActions.logout (manual logout)
      //     - refreshCoordinator.performLogout (refresh failure / mid-flight
      //       termination)
      //
      //   This case alone DOES NOT teardown:
      //     * profile slice (still holds stale data — Phase 4B will add
      //       PROFILE_RESET)
      //     * bootstrap.phase (stays 'ready' — BOOTSTRAP_RESET exists but is
      //       not cascaded from AUTH_LOGOUT today)
      //     * refresh coordinator's `terminated` flag (module-scope, only
      //       cleared by module unload)
      //     * coordinator `inFlight` ref (cleared by finally, but a logout
      //       that races a mid-flight refresh relies on terminated to bail)
      //     * chat slice / userDetails slice / userList slice
      //     * WebSocket connection (App.js useEffect tears down via
      //       userInfo?.access dep — needs USER_LOGOUT to fire to flip)
      //
      //   The hard reload after navigation discards the entire JS context,
      //   resetting all of the above implicitly.
      //
      //   IF a future "soft logout" (no page reload) is introduced, the
      //   caller MUST also dispatch: PROFILE_RESET (Phase 4B), BOOTSTRAP_RESET
      //   + re-dispatch bootstrapApp(), CHAT_*_RESET, manually call
      //   wsManager.disconnect(), and provide a coordinator-reset hook (no
      //   public API for this today). Until that landing, AUTH_LOGOUT must
      //   always be paired with navigation.
      return INITIAL_AUTH_STATE;

    default:
      return state;
  }
};
